<script setup>
import { ref, onMounted, computed } from "vue";

import { apiUrl } from "@/utils/api";
import Swal from "sweetalert2";
import { Swiper, SwiperSlide } from "swiper/vue";
import { Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
</script>
<template>
  <div class="d-flex flex-column flex-column-fluid">
    <!-- Toolbar -->
    <div id="kt_app_toolbar" class="app-toolbar py-3 py-lg-6">
      <div
        id="kt_app_toolbar_container"
        class="app-container container-fluid d-flex flex-stack"
      >
        <div class="page-title d-flex flex-column justify-content-center flex-wrap me-3">
          <h1 class="page-heading text-dark fw-bold fs-3 my-0">تفاصيل الحدث</h1>
          <ul class="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
            <li class="breadcrumb-item text-muted">
              <router-link to="/" class="text-muted text-hover-primary"
                >الرئيسية</router-link
              >
            </li>
            <li class="breadcrumb-item">
              <span class="bullet bg-gray-400 w-5px h-2px"></span>
            </li>
            <li class="breadcrumb-item text-muted">
              {{ event?.title_ar || event?.title_original }}
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- المحتوى -->
    <div id="kt_app_content" class="app-content flex-column-fluid">
      <div id="kt_app_content_container" class="app-container container-fluid">
        <!-- التحميل -->
        <div v-if="isLoading" class="text-center py-5">
          <div class="spinner-border text-success" role="status"></div>
          <p class="mt-3 fw-bold text-secondary">جاري تحميل تفاصيل الحدث...</p>
        </div>

        <!-- التفاصيل -->
        <div v-else-if="event" class="card shadow-lg p-4 mt-4">
          <!-- بانر الحدث -->
          <!-- بنر الحدث -->
          <!-- بنر الحدث -->
          <div class="event-banner mb-4">
            <Swiper
              v-if="event.banners && event.banners.length"
              :modules="[Pagination, Autoplay]"
              :loop="true"
              :autoplay="{ delay: 3000, disableOnInteraction: false }"
              :pagination="{ clickable: true }"
              class="event-swiper"
            >
              
              <SwiperSlide
                v-for="(banner, index) in event.banners"
                :key="index"
                class="banner-slide"
              >
                <img
                  :src="banner.image_url || defaultImage"
                  class="banner-image"
                  @error="(e) => (e.target.src = defaultImage)"
                />
              </SwiperSlide>
              
            </Swiper>

            <!-- في حال لا يوجد بنرات -->
            <div v-else>
              <img
                :src="getEventImage(event.image)"
                class="w-100 rounded"
                style="max-height: 350px; object-fit: cover"
                @error="event.image = defaultImage"
              />
            </div>
          </div>

          <!-- عنوان الحدث -->
          <h2 class="fw-bold text-success mb-3">
            {{ event.title_ar || event.title_original }}
          </h2>

          <!-- أزرار التذكير والمفضلة -->
          <!-- أزرار التذكير والمفضلة -->
          <div class="mb-4 text-end d-flex justify-content-end gap-2 toolbar-buttons">
            <!-- زر التذكير -->
            <button
              class="btn btn-success btn-pill shadow-sm d-flex align-items-center gap-1"
              @click="confirmReminder"
              :disabled="reminderLoading"
            >
              <i class="fa fa-bell text-white"></i>
              <span v-if="!reminderLoading">تعيين تذكير للحدث</span>
              <span v-else class="d-flex align-items-center">
                <div
                  class="spinner-border spinner-border-sm text-light me-1"
                  role="status"
                ></div>
                جاري المعالجة...
              </span>
            </button>

            <!-- زر المفضلة -->
            <button
              class="btn btn-outline-primary btn-pill shadow-sm d-flex align-items-center gap-1"
              :class="isFavorite ? 'btn-danger' : 'btn-outline-secondary'"
              @click="toggleFavorite"
              :disabled="favoriteLoading"
            >
              <i
                class="fa fa-heart"
                :class="isFavorite ? 'text-white' : 'text-muted'"
              ></i>
              <span v-if="favoriteLoading" class="d-flex align-items-center">
                <div class="spinner-border spinner-border-sm me-1" role="status"></div>
                جاري المعالجة...
              </span>
              <span v-else>
                {{ isFavorite ? "إزالة من المفضلة" : "إضافة إلى المفضلة" }}
              </span>
            </button>
          </div>

          <!-- معلومات أساسية -->
          <div class="row gy-3">
            <div class="col-md-6 border rounded p-3 bg-light">
              <strong>نوع الحدث:</strong>
              <p class="mb-0">{{ event.type || "غير محدد" }}</p>
            </div>
            <div class="col-md-6 border rounded p-3 bg-light">
              <strong>المستوى:</strong>
              <p class="mb-0">{{ event.level || "غير محدد" }}</p>
            </div>
            <div class="col-md-6 border rounded p-3 bg-light">
              <strong>نظام الحدث:</strong>
              <p class="mb-0">{{ event.mode || "غير محدد" }}</p>
            </div>
            <div class="col-md-6 border rounded p-3 bg-light">
              <strong>المدينة:</strong>
              <p class="mb-0">{{ event.city || "-" }}</p>
            </div>
            <div class="col-md-6 border rounded p-3 bg-light">
              <strong>الدولة:</strong>
              <p class="mb-0">{{ event.country || "-" }}</p>
            </div>
            <div class="col-md-6 border rounded p-3 bg-light">
              <strong>تاريخ البدء:</strong>
              <p class="mb-0">{{ formatDate(event.start_date) }}</p>
            </div>
            <div class="col-md-6 border rounded p-3 bg-light" v-if="event.end_date">
              <strong>تاريخ الانتهاء:</strong>
              <p class="mb-0">{{ formatDate(event.end_date) }}</p>
            </div>
          </div>

          <!-- وصف الحدث -->
          <div v-if="event.description" class="mt-4">
            <h5 class="fw-bold text-dark mb-2">وصف الحدث:</h5>
            <p>{{ event.description }}</p>
          </div>

          <!-- المتحدثون -->
          <div v-if="event.speakers && event.speakers.length" class="mt-4">
            <h5 class="fw-bold text-dark mb-3">المتحدثون:</h5>
            <div class="row g-3">
              <div v-for="speaker in event.speakers" :key="speaker.id" class="col-md-4">
                <div class="card h-100 text-center p-3">
                  <img
                    :src="speaker.photo_url || defaultImage"
                    class="rounded-circle mb-2"
                    style="width: 100px; height: 100px; object-fit: cover"
                  />
                  <h6 style="text-align: right;" class="fw-bold mb-0">{{ speaker.name }}</h6>
                  <small style="text-align: right;" class="text-muted">{{ speaker.position }}</small>
                  <p style="text-align: right;" class="mt-2 mb-0">{{ speaker.bio }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- الفئات الفرعية -->
          <div v-if="event.subcategories && event.subcategories.length" class="mt-4">
            <h5 class="fw-bold text-dark mb-2">الفئات الفرعية:</h5>
            <div class="d-flex flex-wrap gap-2">
              <span
                v-for="sub in event.subcategories"
                :key="sub.id"
                class="badge bg-success px-3 py-2 fs-7"
              >
                {{ sub.title }}
              </span>
            </div>
          </div>
        </div>

        <!-- لم يتم العثور على حدث -->
        <div v-else class="alert alert-warning mt-5 text-center">
          لم يتم العثور على تفاصيل الحدث.
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from "vue";

import { apiUrl } from "@/utils/api";
import Swal from "sweetalert2";
import { Swiper, SwiperSlide } from "swiper/vue";
import { Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";

export default {
  data() {
    return {
      event: null,
      isLoading: true,
      eventId: null,
      defaultImage: "/assets/img/acad-icon.png",
      token: localStorage.getItem("access_token"),
      student: null,
      reminderLoading: false,
      favoriteLoading: false,
      isFavorite: false,
    };
  },
  mounted() {
    this.eventId = new URLSearchParams(window.location.search).get("id");
    this.fetchEventDetails();
    this.fetchStudentDetails();
  },
  methods: {
    async fetchEventDetails() {
      try {
        const response = await fetch(`${apiUrl}/events/${this.eventId}`);
        const res = await response.json();
        this.event = res.data;
      } catch (error) {
        console.error("Error fetching event:", error);
      } finally {
        this.isLoading = false;
      }
    },

    async fetchStudentDetails() {
      try {
        const response = await fetch(`${apiUrl}/desktop/my-courses`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${this.token}`,
            "Content-Type": "application/json",
          },
        });
        if (response.status === 302 || response.redirected || !response.ok) {
          this.student = null;
        } else {
          const data = await response.json();
          this.student = data.student;
          this.checkFavoriteStatus();
        }
      } catch (error) {
        console.error("Error fetching student details:", error);
      }
    },

    async confirmReminder() {
      if (!this.token) {
        Swal.fire("خطأ", "يجب تسجيل الدخول لتعيين تذكير للحدث.", "error");
        return;
      }

      const result = await Swal.fire({
        title: "تأكيد التذكير",
        text: "هل ترغب في تعيين تذكير لهذا الحدث؟",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "نعم، عيّن التذكير",
        cancelButtonText: "إلغاء",
        confirmButtonColor: "#198754",
        cancelButtonColor: "#d33",
      });

      if (result.isConfirmed) {
        this.setReminder();
      }
    },

    async setReminder() {
      this.reminderLoading = true;
      try {
        const requestBody = this.student?.id ? { user_id: this.student.id } : {};
        const response = await fetch(`${apiUrl}/events/${this.eventId}/remind`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${this.token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        });

        const res = await response.json();

        if (response.ok) {
          Swal.fire("تم بنجاح", res.message || "تم تعيين التذكير بنجاح.", "success");
        } else if (response.status === 401) {
          Swal.fire("خطأ", "يجب تسجيل الدخول لتعيين تذكير للحدث.", "error");
        } else {
          Swal.fire("خطأ", res.message || "حدث خطأ أثناء تعيين التذكير.", "error");
        }
      } catch (error) {
        Swal.fire("خطأ", "تعذر الاتصال بالخادم.", "error");
        console.error("Error setting reminder:", error);
      } finally {
        this.reminderLoading = false;
      }
    },

    async checkFavoriteStatus() {
      if (!this.token) return;
      try {
        const response = await fetch(`${apiUrl}/events/${this.eventId}/favorite/check`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${this.token}`,
            "Content-Type": "application/json",
          },
        });
        const data = await response.json();
        this.isFavorite = data.data.is_favorite || false;
      } catch (error) {
        console.error("Error checking favorite:", error);
      }
    },

    async toggleFavorite() {
      if (!this.token) {
        Swal.fire("تنبيه", "يجب تسجيل الدخول لإدارة المفضلة.", "warning");
        return;
      }

      this.favoriteLoading = true;

      if (!this.isFavorite) {
        // إضافة إلى المفضلة
        /*const confirmAdd = await Swal.fire({
          title: "تأكيد",
          text: "هل ترغب في إضافة الحدث إلى المفضلة؟",
          icon: "question",
          showCancelButton: true,
          confirmButtonText: "نعم",
          cancelButtonText: "إلغاء",
        });
        if (!confirmAdd.isConfirmed) return (this.favoriteLoading = false);*/

        await this.addToFavorites();
      } else {
        // إزالة من المفضلة
        /*const confirmRemove = await Swal.fire({
          title: "تأكيد",
          text: "هل ترغب في إزالة الحدث من المفضلة؟",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "نعم",
          cancelButtonText: "إلغاء",
        });
        if (!confirmRemove.isConfirmed) return (this.favoriteLoading = false);*/

        await this.removeFromFavorites();
      }

      this.favoriteLoading = false;
    },

    async addToFavorites() {
      try {
        const response = await fetch(`${apiUrl}/events/${this.eventId}/favorite`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${this.token}`,
            "Content-Type": "application/json",
          },
        });

        const data = await response.json();
        if (response.ok) {
          this.isFavorite = true;
          //Swal.fire("تم", data.message || "تمت إضافة الحدث إلى المفضلة", "success");
        } else {
          Swal.fire("خطأ", data.message || "فشل في الإضافة", "error");
        }
      } catch (error) {
        console.error("Error adding favorite:", error);
        //Swal.fire("خطأ", "حدث خطأ أثناء الإضافة", "error");
      }
    },

    async removeFromFavorites() {
      try {
        const response = await fetch(`${apiUrl}/events/${this.eventId}/favorite`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${this.token}`,
            "Content-Type": "application/json",
          },
        });

        const data = await response.json();
        if (response.ok) {
          this.isFavorite = false;
          //Swal.fire("تم", data.message || "تمت الإزالة من المفضلة", "success");
        } else {
          //Swal.fire("خطأ", data.message || "فشل في الإزالة", "error");
        }
      } catch (error) {
        console.error("Error removing favorite:", error);
        //Swal.fire("خطأ", "حدث خطأ أثناء الإزالة", "error");
      }
    },

    getEventImage(img) {
      if (!img) return this.defaultImage;
      return img.startsWith("http") ? img : `${apiUrl}${img}`;
    },

    formatDate(dateStr) {
      if (!dateStr) return "غير محدد";
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },
  },
};
</script>
<style>
/* ========== Main Card ========== */
.card {
  border-radius: 24px;
  background: linear-gradient(145deg, #ffffff 0%, #fafffe 100%);
  border: 1px solid rgba(0, 196, 68, 0.08);
  position: relative;
  overflow: hidden;
}
.card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 5px;
  background: linear-gradient(90deg, #00c444, #00e050, #00c444);
  background-size: 200% 100%;
  animation: gradientSlide 3s linear infinite;
}
@keyframes gradientSlide {
  0% { background-position: 0% 50%; }
  100% { background-position: 200% 50%; }
}
.card.shadow-lg {
  box-shadow: 0 20px 60px rgba(0, 196, 68, 0.1), 0 8px 25px rgba(0, 0, 0, 0.06) !important;
  border-top: none;
}

/* ========== Toolbar Buttons ========== */
.toolbar-buttons .btn-pill {
  border-radius: 999px;
  font-weight: 600;
  padding: 10px 24px;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  position: relative;
  overflow: hidden;
}
.toolbar-buttons .btn-pill::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s ease;
}
.toolbar-buttons .btn-pill:hover::before {
  left: 100%;
}
.toolbar-buttons .btn-pill:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}
.toolbar-buttons .btn-success {
  background: linear-gradient(135deg, #00c444 0%, #00a43c 100%);
  border: none;
  color: #fff;
  box-shadow: 0 4px 15px rgba(0, 196, 68, 0.3);
}
.toolbar-buttons .btn-success:hover {
  background: linear-gradient(135deg, #00d44a 0%, #00b844 100%);
  box-shadow: 0 8px 25px rgba(0, 196, 68, 0.4);
}
.toolbar-buttons .btn-danger {
  background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
  border: none;
  color: #fff;
  box-shadow: 0 4px 15px rgba(220, 53, 69, 0.3);
}
.toolbar-buttons .btn-danger:hover {
  background: linear-gradient(135deg, #e04555 0%, #d32f3f 100%);
  box-shadow: 0 8px 25px rgba(220, 53, 69, 0.4);
}
.toolbar-buttons .btn-outline-secondary {
  border: 2px solid #6c757d;
  color: #6c757d;
  background: transparent;
}
.toolbar-buttons .btn-outline-secondary:hover {
  background: linear-gradient(135deg, #6c757d 0%, #5a6268 100%);
  color: #fff;
}

/* ========== Event Banner ========== */
.event-banner {
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 196, 68, 0.12);
}
.event-swiper {
  width: 100%;
  height: 380px;
}
.banner-slide {
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(145deg, #f8fffe 0%, #f0fff5 100%);
  position: relative;
  overflow: hidden;
}
.banner-slide::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(180deg, transparent 70%, rgba(0, 0, 0, 0.3) 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
  z-index: 1;
}
.banner-slide:hover::before {
  opacity: 1;
}
.banner-image {
  width: 100%;
  height: 380px;
  object-fit: cover;
  border-radius: 20px;
  transition: transform 0.5s ease;
}
.banner-slide:hover .banner-image {
  transform: scale(1.03);
}

/* ========== Swiper Pagination ========== */
.swiper-pagination-bullet {
  background: rgba(0, 196, 68, 0.3);
  opacity: 1;
  width: 10px;
  height: 10px;
  margin: 0 4px !important;
  transition: all 0.3s ease;
}
.swiper-pagination-bullet-active {
  background: linear-gradient(135deg, #00c444 0%, #00e050 100%);
  width: 28px;
  border-radius: 6px;
}

/* ========== Title ========== */
h2.text-success {
  background: linear-gradient(135deg, #00c444 0%, #00a43c 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-size: 1.75rem;
}

/* ========== Info Boxes ========== */
.border.rounded.p-3.bg-light {
  border: none !important;
  border-radius: 16px !important;
  background: linear-gradient(145deg, #f8fffe 0%, #f0fff5 100%) !important;
  border-left: 4px solid #00c444 !important;
  padding: 16px 20px !important;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 196, 68, 0.06);
}
.border.rounded.p-3.bg-light:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 196, 68, 0.12);
  border-left-width: 6px !important;
}
.border.rounded.p-3.bg-light strong {
  color: #00a43c;
  font-size: 0.9rem;
}
.border.rounded.p-3.bg-light p {
  color: #1a1a2e;
  font-weight: 500;
  margin-top: 6px;
}

/* ========== Section Headings ========== */
h5.fw-bold.text-dark {
  position: relative;
  padding-right: 16px;
  color: #1a1a2e !important;
}
h5.fw-bold.text-dark::before {
  content: '';
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 100%;
  background: linear-gradient(180deg, #00c444 0%, #00e050 100%);
  border-radius: 4px;
}

/* ========== Speakers Cards ========== */
.card.h-100.text-center.p-3 {
  border-radius: 20px !important;
  border: 1px solid rgba(0, 196, 68, 0.1) !important;
  background: linear-gradient(145deg, #ffffff 0%, #fafffe 100%);
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 196, 68, 0.06);
}
.card.h-100.text-center.p-3:hover {
  transform: translateY(-6px);
  box-shadow: 0 12px 30px rgba(0, 196, 68, 0.15);
  border-color: rgba(0, 196, 68, 0.25) !important;
}
.card.h-100.text-center.p-3 img {
  border: 3px solid rgba(0, 196, 68, 0.2);
  box-shadow: 0 4px 15px rgba(0, 196, 68, 0.15);
}

/* ========== Badges ========== */
.badge.bg-success {
  background: linear-gradient(135deg, #00c444 0%, #00a43c 100%) !important;
  border-radius: 999px;
  padding: 8px 16px;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(0, 196, 68, 0.25);
  transition: all 0.3s ease;
}
.badge.bg-success:hover {
  transform: translateY(-2px) scale(1.05);
  box-shadow: 0 4px 15px rgba(0, 196, 68, 0.35);
}

/* ========== Text Colors ========== */
.text-success { color: #00c444 !important; }
.btn-success {
  background: linear-gradient(135deg, #00c444 0%, #00a43c 100%) !important;
  border: none !important;
  box-shadow: 0 4px 20px rgba(0, 196, 68, 0.35);
}
.btn-success:hover {
  background: linear-gradient(135deg, #00d44a 0%, #00b844 100%) !important;
  transform: translateY(-3px);
  box-shadow: 0 8px 30px rgba(0, 196, 68, 0.45);
}

/* ========== Links ========== */
a { 
  color: #00a43c; 
  transition: all 0.3s ease;
  text-decoration: none;
}
a:hover { 
  color: #00c444; 
  text-decoration: underline;
}

/* ========== Form Controls ========== */
.form-control:focus,
.form-select:focus {
  border-color: #00c444 !important;
  box-shadow: 0 0 0 4px rgba(0, 196, 68, 0.12) !important;
}

/* ========== Loading Spinner ========== */
.spinner-border.text-success {
  color: #00c444 !important;
  width: 3rem;
  height: 3rem;
}
.text-center.py-5 {
  background: linear-gradient(145deg, #f8fffe 0%, #f0fff5 100%);
  border-radius: 20px;
  padding: 60px !important;
  box-shadow: 0 4px 20px rgba(0, 196, 68, 0.08);
}

/* ========== Alert ========== */
.alert-warning {
  border-radius: 20px;
  border: none;
  background: linear-gradient(135deg, #fff8e6 0%, #fff3d4 100%);
  box-shadow: 0 8px 30px rgba(255, 193, 7, 0.15);
  padding: 30px;
}

/* ========== Row Animation ========== */
.row.gy-3 > div {
  animation: fadeInUp 0.5s ease forwards;
  opacity: 0;
}
.row.gy-3 > div:nth-child(1) { animation-delay: 0.05s; }
.row.gy-3 > div:nth-child(2) { animation-delay: 0.1s; }
.row.gy-3 > div:nth-child(3) { animation-delay: 0.15s; }
.row.gy-3 > div:nth-child(4) { animation-delay: 0.2s; }
.row.gy-3 > div:nth-child(5) { animation-delay: 0.25s; }
.row.gy-3 > div:nth-child(6) { animation-delay: 0.3s; }
.row.gy-3 > div:nth-child(7) { animation-delay: 0.35s; }
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(15px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ========== Responsive ========== */
@media (max-width: 768px) {
  .card {
    border-radius: 18px;
    padding: 16px !important;
  }
  .event-swiper,
  .banner-image {
    height: 250px;
  }
  .event-banner {
    border-radius: 16px;
  }
  h2.text-success {
    font-size: 1.4rem;
  }
  .border.rounded.p-3.bg-light {
    padding: 12px 16px !important;
  }
  .toolbar-buttons {
    flex-direction: column;
  }
  .toolbar-buttons .btn-pill {
    width: 100%;
    justify-content: center;
  }
}

/* ========== Scrollbar ========== */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}
::-webkit-scrollbar-track {
  background: #f0fff5;
  border-radius: 10px;
}
::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, #00c444 0%, #00a43c 100%);
  border-radius: 10px;
}
::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, #00d44a 0%, #00b844 100%);
}

/* ========== DARK MODE ========== */
[data-bs-theme="dark"] .card,
[data-theme="dark"] .card,
.dark .card {
  background: linear-gradient(145deg, #242526 0%, #1a1b1d 100%);
  border-color: rgba(0, 196, 68, 0.15);
}
[data-bs-theme="dark"] .card.shadow-lg,
[data-theme="dark"] .card.shadow-lg,
.dark .card.shadow-lg {
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4), 0 8px 25px rgba(0, 196, 68, 0.1) !important;
}

[data-bs-theme="dark"] .event-banner,
[data-theme="dark"] .event-banner,
.dark .event-banner {
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
}
[data-bs-theme="dark"] .banner-slide,
[data-theme="dark"] .banner-slide,
.dark .banner-slide {
  background: linear-gradient(145deg, #2d2e30 0%, #242526 100%);
}

[data-bs-theme="dark"] h2.text-success,
[data-theme="dark"] h2.text-success,
.dark h2.text-success {
  background: linear-gradient(135deg, #00e050 0%, #00c444 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

[data-bs-theme="dark"] .border.rounded.p-3.bg-light,
[data-theme="dark"] .border.rounded.p-3.bg-light,
.dark .border.rounded.p-3.bg-light {
  background: linear-gradient(145deg, #2d2e30 0%, #242526 100%) !important;
  border-left-color: #00c444 !important;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
}
[data-bs-theme="dark"] .border.rounded.p-3.bg-light:hover,
[data-theme="dark"] .border.rounded.p-3.bg-light:hover,
.dark .border.rounded.p-3.bg-light:hover {
  box-shadow: 0 8px 25px rgba(0, 196, 68, 0.15);
}
[data-bs-theme="dark"] .border.rounded.p-3.bg-light strong,
[data-theme="dark"] .border.rounded.p-3.bg-light strong,
.dark .border.rounded.p-3.bg-light strong {
  color: #00e050;
}
[data-bs-theme="dark"] .border.rounded.p-3.bg-light p,
[data-theme="dark"] .border.rounded.p-3.bg-light p,
.dark .border.rounded.p-3.bg-light p {
  color: #e4e6eb;
}

[data-bs-theme="dark"] h5.fw-bold.text-dark,
[data-theme="dark"] h5.fw-bold.text-dark,
.dark h5.fw-bold.text-dark {
  color: #e4e6eb !important;
}
[data-bs-theme="dark"] h5.fw-bold.text-dark::before,
[data-theme="dark"] h5.fw-bold.text-dark::before,
.dark h5.fw-bold.text-dark::before {
  background: linear-gradient(180deg, #00e050 0%, #00c444 100%);
}

[data-bs-theme="dark"] .card.h-100.text-center.p-3,
[data-theme="dark"] .card.h-100.text-center.p-3,
.dark .card.h-100.text-center.p-3 {
  background: linear-gradient(145deg, #242526 0%, #1a1b1d 100%);
  border-color: rgba(0, 196, 68, 0.15) !important;
}
[data-bs-theme="dark"] .card.h-100.text-center.p-3:hover,
[data-theme="dark"] .card.h-100.text-center.p-3:hover,
.dark .card.h-100.text-center.p-3:hover {
  box-shadow: 0 12px 30px rgba(0, 196, 68, 0.2);
}
[data-bs-theme="dark"] .card.h-100.text-center.p-3 h6,
[data-theme="dark"] .card.h-100.text-center.p-3 h6,
.dark .card.h-100.text-center.p-3 h6 {
  color: #e4e6eb;
}
[data-bs-theme="dark"] .card.h-100.text-center.p-3 small,
[data-bs-theme="dark"] .card.h-100.text-center.p-3 p,
[data-theme="dark"] .card.h-100.text-center.p-3 small,
[data-theme="dark"] .card.h-100.text-center.p-3 p,
.dark .card.h-100.text-center.p-3 small,
.dark .card.h-100.text-center.p-3 p {
  color: #b0b3b8;
}

[data-bs-theme="dark"] .toolbar-buttons .btn-outline-secondary,
[data-theme="dark"] .toolbar-buttons .btn-outline-secondary,
.dark .toolbar-buttons .btn-outline-secondary {
  border-color: #6c757d;
  color: #b0b3b8;
}

[data-bs-theme="dark"] .text-center.py-5,
[data-theme="dark"] .text-center.py-5,
.dark .text-center.py-5 {
  background: linear-gradient(145deg, #242526 0%, #1a1b1d 100%);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}
[data-bs-theme="dark"] .text-center.py-5 p,
[data-theme="dark"] .text-center.py-5 p,
.dark .text-center.py-5 p {
  color: #b0b3b8;
}

[data-bs-theme="dark"] .alert-warning,
[data-theme="dark"] .alert-warning,
.dark .alert-warning {
  background: linear-gradient(135deg, #3d3520 0%, #4a3f1a 100%);
  color: #ffd666;
}

[data-bs-theme="dark"] .breadcrumb-item.text-muted,
[data-theme="dark"] .breadcrumb-item.text-muted,
.dark .breadcrumb-item.text-muted {
  color: #b0b3b8 !important;
}
[data-bs-theme="dark"] .breadcrumb-item a,
[data-theme="dark"] .breadcrumb-item a,
.dark .breadcrumb-item a {
  color: #00e050 !important;
}

[data-bs-theme="dark"] .page-heading,
[data-theme="dark"] .page-heading,
.dark .page-heading {
  color: #e4e6eb;
}

[data-bs-theme="dark"] a,
[data-theme="dark"] a,
.dark a {
  color: #00e050;
}
[data-bs-theme="dark"] a:hover,
[data-theme="dark"] a:hover,
.dark a:hover {
  color: #8bffb3;
}

[data-bs-theme="dark"] .swiper-pagination-bullet,
[data-theme="dark"] .swiper-pagination-bullet,
.dark .swiper-pagination-bullet {
  background: rgba(0, 196, 68, 0.4);
}
[data-bs-theme="dark"] .swiper-pagination-bullet-active,
[data-theme="dark"] .swiper-pagination-bullet-active,
.dark .swiper-pagination-bullet-active {
  background: linear-gradient(135deg, #00e050 0%, #00c444 100%);
}

[data-bs-theme="dark"] ::-webkit-scrollbar-track,
[data-theme="dark"] ::-webkit-scrollbar-track,
.dark ::-webkit-scrollbar-track {
  background: #242526;
}
</style>
